import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React, { useContext } from 'react';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import NineteenCta, {
  nineteenCtaThemes
} from 'components/nineteen/nineteen-cta/nineteen-cta';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_FieldParagraphNineteenFooterFieldAdditionalResources,
  Drupal_FieldParagraphNineteenFooterFieldNineteenCtas,
  Drupal_ParagraphNineteenFooter
} from 'generated/graphql';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';

interface Props {
  entity: Drupal_ParagraphNineteenFooter;
}

const NineteenFooter = ({
  entity: { fieldHeader, fieldAdditionalResources, fieldNineteenCtas }
}: Props) => {
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_ct_cs_additional_resources
              }
            }
          }
        }
      }
    `
  );

  const {
    language: { i18nKey }
  } = useContext(LayoutContext);
  const i18n = i18nFunct(i18nData, i18nKey);

  return (
    <footer
      className="nineteen-footer"
      css={theme => ({
        backgroundColor: theme.colors.indigo[100],
        padding: `${theme.space[7]}px 0`,
        [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
          padding: `${theme.space[9]}px 0`
        }
      })}
    >
      <LucidContainer
        flex
        css={theme => ({
          flexWrap: 'wrap',
          [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
            flexWrap: 'nowrap'
          }
        })}
      >
        <Box
          className="nineteen-footer__blurb"
          flex={{
            default: '0 1 100%',
            tabletSmall: '0 1 40%',
            tablet: '0 1 50%'
          }}
          marginBottom={{ default: 2, tabletSmall: 0 }}
        >
          <Heading as="h2" color="white" mt={0} variant="subheadS">
            {fieldHeader}
          </Heading>
          {(fieldNineteenCtas as Drupal_FieldParagraphNineteenFooterFieldNineteenCtas[]).map(
            ({ entity }, index) => (
              <NineteenCta
                key={entity!.entityId}
                entity={entity!}
                primary={index === 0}
                theme={nineteenCtaThemes.retro}
              />
            )
          )}
        </Box>
        <Box flex={{ default: '0 1 100%', tabletSmall: '0 1 20rem' }}>
          <Text
            css={(theme: LucidTheme) => ({
              color: theme.colors.white,
              fontWeight: theme.fontWeights.bolder
            })}
            textStyles="bodySmall"
          >
            {i18n('mktg-ct-cs-additional-resources')}
            :
          </Text>
          <ul
            css={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}
          >
            {(fieldAdditionalResources as Drupal_FieldParagraphNineteenFooterFieldAdditionalResources[]).map(
              ({ url, title }) => (
                <li
                  key={title}
                  css={theme => ({ marginBottom: theme.space[3] })}
                >
                  <Link
                    inverse
                    className="nineteen-footer__resource-link"
                    to={url!.path!}
                  >
                    {title}
                  </Link>
                </li>
              )
            )}
          </ul>
        </Box>
      </LucidContainer>
    </footer>
  );
};

export default NineteenFooter;

export const query = graphql`
  fragment nineteenFooter on Drupal_ParagraphNineteenFooter {
    fieldHeader
    fieldAdditionalResources {
      url {
        path
      }
      title
    }
    fieldNineteenCtas {
      entity {
        ...nineteenCta
      }
    }
  }
`;
